import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, LayoutGrid, List, Map } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Avatar } from '@/components/ui/avatar';
import {
  useInfiniteProperties,
  useProperties,
  usePropertiesStats,
  useProperty,
  type PropertyRow,
} from '@/hooks/data/useProperties';
import { useInfiniteScrollSentinel } from '@/hooks/useInfiniteScrollSentinel';
import { CreatePropertyDialog } from '@/components/properties/CreatePropertyDialog';
import { EditPropertyDialog } from '@/components/properties/EditPropertyDialog';
import { ViewPropertyDialog } from '@/components/properties/ViewPropertyDialog';
import { PropertyCard } from '@/components/properties/PropertyCard';
import { lazy, Suspense } from 'react';
import { PropertyCompareDialog } from '@/components/properties/PropertyCompareDialog';
import { useSearchTracker } from '@/hooks/useSearchTracker';
import { humanizeApiError } from '@/lib/errorMessages';
import { formatMoney, formatNumber } from '@/lib/formatLocale';
import {
  useInfiniteSiteListings,
  useSiteListingCounts,
  useSiteListingImagesByProperty,
  type SiteListingRow,
} from '@/hooks/data/useSiteListings';
import { SiteListingCard } from '@/components/properties/SiteListingCard';
import { SiteListingDialog } from '@/components/properties/SiteListingDialog';
import { AddListingToCrmDialog } from '@/components/properties/AddListingToCrmDialog';
import {
  isSiteSegment,
  PROPERTY_SEGMENTS,
  readStoredSegment,
  writeStoredSegment,
  type PropertySegment,
} from '@/lib/propertySegments';

// Lazy-load Leaflet — adds ~50kb gzipped only when the map view is opened.
const PropertyMap = lazy(() =>
  import('@/components/properties/PropertyMap').then((m) => ({ default: m.PropertyMap })),
);

// v2 — five "operator-visible" buckets the chip strip toggles, each mapping
// to a sub-set of marketing_stage enum values. Order matches the canonical
// lifecycle (Akquise → In Vermarktung → Anbot → Notar → Verkauft).
const STAGE_BUCKETS = [
  { key: 'akquise',  enums: ['lead', 'aufnahme'] as const,                fallback: 'Akquise' },
  { key: 'vermarkt', enums: ['active'] as const,                          fallback: 'In Vermarktung' },
  { key: 'anbot',    enums: ['kaufanbot_angenommen'] as const,            fallback: 'Anbot angenommen' },
  { key: 'notar',    enums: ['notar_termin_geplant'] as const,            fallback: 'Notar geplant' },
  { key: 'verkauft', enums: ['notariell_verkauft', 'sold'] as const,      fallback: 'Verkauft' },
] as const;
type BucketKey = (typeof STAGE_BUCKETS)[number]['key'];

interface PropertiesSearch {
  stages?: BucketKey[];
  /** Deep-link from global search — opens the detail dialog for this id. */
  open?: string;
}

export const Route = createFileRoute('/_app/properties')({
  validateSearch: (s: Record<string, unknown>): PropertiesSearch => {
    const out: PropertiesSearch = {};
    const raw = typeof s.stages === 'string' ? s.stages.split(',') : [];
    const valid = raw.filter((k): k is BucketKey =>
      STAGE_BUCKETS.some((b) => b.key === k),
    );
    if (valid.length > 0) out.stages = valid;
    if (typeof s.open === 'string' && s.open) out.open = s.open;
    return out;
  },
  component: PropertiesPage,
});

const safeStorage = (): Storage | undefined => {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
};

function PropertiesPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const search0 = Route.useSearch();
  const navigate = Route.useNavigate();
  const selectedBuckets = search0.stages ?? [];
  const toggleBucket = (k: BucketKey) => {
    const next = selectedBuckets.includes(k)
      ? selectedBuckets.filter((x) => x !== k)
      : [...selectedBuckets, k];
    navigate({
      search: () => (next.length === 0 ? {} : { stages: next }),
      replace: true,
    });
  };

  // Der Bereich ist eine Vorliebe und bleibt gespeichert, der Suchtext nie.
  // Ein Link mit Phasen-Filter meint Objekte aus dem CRM, nicht Inserate.
  const [segment, setSegmentState] = useState<PropertySegment>(() =>
    search0.stages?.length ? 'sve' : readStoredSegment(safeStorage()),
  );
  const siteMode = isSiteSegment(segment);
  const selectSegment = (next: PropertySegment) => {
    setSegmentState(next);
    writeStoredSegment(safeStorage(), next);
    setCompareIds(new Set());
    // Phasen-Chips gibt es bei Inseraten nicht; ein unsichtbarer Filter darf nicht bleiben.
    if (isSiteSegment(next) && selectedBuckets.length > 0) {
      navigate({ search: () => ({}), replace: true });
    }
  };
  const stageCount = selectedBuckets.length;
  useEffect(() => {
    if (stageCount > 0 && isSiteSegment(segment)) setSegmentState('sve');
    // Nur wenn ein Link Phasen mitbringt, nicht bei jedem Bereichswechsel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageCount]);

  const [search, setSearch] = useState('');
  const [city, setCity] = useState<string>('');
  // Batch AQ — Moji/Svi scope toggle. Default to mine for makler, all
  // for admin / call-center. Persisted in localStorage so the choice
  // survives page reload.
  const [scope, setScope] = useState<'mine' | 'all'>(() => {
    if (typeof window === 'undefined') return 'all';
    const stored = localStorage.getItem('properties.scope') as 'mine' | 'all' | null;
    if (stored === 'mine' || stored === 'all') return stored;
    return profile?.role === 'makler' ? 'mine' : 'all';
  });
  // Bei Inseraten gilt zuerst derselbe Bestand wie auf der Website, unabhängig
  // von der gespeicherten Wahl. Dieser Schalter wird nicht gespeichert.
  const [siteScope, setSiteScope] = useState<'mine' | 'all'>('all');
  const activeScope = siteMode ? siteScope : scope;
  // Gespeichert wird nur die eigene Wahl am Schalter. „Alle anzeigen" gilt für
  // diesen Besuch und lässt eine gespeicherte Vorliebe „Meine" stehen.
  const setActiveScope = (next: 'mine' | 'all') => {
    if (siteMode) {
      setSiteScope(next);
      return;
    }
    setScope(next);
    try {
      safeStorage()?.setItem('properties.scope', next);
    } catch {
      // Nur eine Vorliebe.
    }
  };
  const [openListing, setOpenListing] = useState<SiteListingRow | null>(null);
  const [addingListing, setAddingListing] = useState<SiteListingRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PropertyRow | null>(null);
  const [viewing, setViewing] = useState<PropertyRow | null>(null);

  // Deep-link from global search: /properties?open=<id> fetches the property
  // and opens its detail dialog, then strips the param.
  const { data: deepLinkProperty } = useProperty(search0.open);
  useEffect(() => {
    if (deepLinkProperty) {
      setViewing(deepLinkProperty);
      navigate({ search: (p) => ({ ...p, open: undefined }), replace: true });
    }
  }, [deepLinkProperty, navigate]);

  const [view, setView] = useState<'grid' | 'list' | 'map'>('grid');
  const [compareIds, setCompareIds] = useState<Set<string>>(new Set());
  const [compareOpen, setCompareOpen] = useState(false);
  const toggleCompare = (id: string) =>
    setCompareIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 4) next.add(id);
      return next;
    });

  // Resolve the active chip selection into a flat list of enum stages for
  // the API filter.
  const effectiveStages = selectedBuckets.flatMap(
    (k) => STAGE_BUCKETS.find((b) => b.key === k)?.enums ?? [],
  ) as PropertyRow['marketing_stage'][];

  const {
    data: pageData,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteProperties({
    ...(search ? { search } : {}),
    ...(effectiveStages.length > 0 ? { marketingStages: effectiveStages } : {}),
    ...(city ? { city } : {}),
    ...(scope === 'mine' && profile?.id ? { agentId: profile.id } : {}),
    ...(segment === 'nije_na_sajtu' ? { onSite: false as const } : {}),
  }, 50, { enabled: !siteMode });
  const items = useMemo<PropertyRow[]>(
    () => pageData?.pages.flatMap((p) => p.items) ?? [],
    [pageData],
  );
  const sentinelRef = useInfiniteScrollSentinel<HTMLDivElement>(
    () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    !!hasNextPage,
  );

  const siteQuery = useInfiniteSiteListings(
    {
      segment: segment === 'samo_na_sajtu' ? 'site_only' : 'on_site',
      scope: siteScope,
      ...(search ? { search } : {}),
    },
    { enabled: siteMode },
  );
  const siteItems = useMemo<SiteListingRow[]>(
    () => siteQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [siteQuery.data],
  );
  const siteTotal = siteQuery.data?.pages[0]?.total ?? 0;
  const siteSentinelRef = useInfiniteScrollSentinel<HTMLDivElement>(
    () => {
      if (siteQuery.hasNextPage && !siteQuery.isFetchingNextPage) siteQuery.fetchNextPage();
    },
    !!siteQuery.hasNextPage,
  );
  const { data: counts } = useSiteListingCounts(); // undefined: lädt noch oder ist gescheitert
  const { data: listingImages } = useSiteListingImagesByProperty(segment === 'sve');

  const segmentCount: Record<PropertySegment, number | undefined> = {
    na_sajtu: counts?.on_site,
    samo_na_sajtu: counts?.site_only,
    nije_na_sajtu: counts?.not_on_site,
    sve: counts?.all,
  };

  // Eine verengte Sicht sagt, dass sie verengt ist, und führt mit einem Klick zu allem.
  const filtersActive = siteMode
    ? !!search || siteScope === 'mine'
    : !!search || !!city || selectedBuckets.length > 0 || scope === 'mine';
  const showAll = () => {
    setSearch('');
    setCity('');
    setSiteScope('all');
    setScope('all');
    if (selectedBuckets.length > 0) navigate({ search: () => ({}), replace: true });
    selectSegment('sve');
  };
  const shownLabel = siteMode
    ? formatNumber(siteTotal)
    : segment === 'nije_na_sajtu' && !filtersActive && counts
      ? formatNumber(counts.not_on_site)
      : `${formatNumber(items.length)}${hasNextPage ? '+' : ''}`;
  const narrowed = segment !== 'sve' || filtersActive;
  const listLoading = siteMode ? siteQuery.isLoading : isLoading;
  const listError = siteMode ? siteQuery.error : error;
  // Ohne bekannte Gesamtzahl keine erfundene „0".
  const stripTotal = filtersActive ? segmentCount[segment] : counts?.all;

  const openSiteListing = (l: SiteListingRow) => {
    if (l.state === 'linked' && l.linked_property) {
      navigate({ search: (p) => ({ ...p, open: l.linked_property!.id }), replace: true });
    } else {
      setOpenListing(l);
    }
  };

  // Batch ZR (Task 3) — search history
  useSearchTracker(
    'properties',
    search,
    { stages: selectedBuckets, city, view, segment },
    siteMode ? siteItems.length : items.length,
  );

  const isAdmin = profile?.role === 'admin';
  // KPI counts come from the server-side aggregate so they reflect the
  // true corpus (not the 100-row default page slice). Hook ships as
  // `usePropertiesStats` — mirrors useClientsStats.
  const { data: statsRaw } = usePropertiesStats();
  const ms = statsRaw?.by_marketing_stage ?? {};
  const stats = {
    total: statsRaw?.total ?? 0,
    active: ms.active ?? 0,
    leads: (ms.lead ?? 0) + (ms.aufnahme ?? 0),
    inOffer: (ms.kaufanbot_angenommen ?? 0) + (ms.notar_termin_geplant ?? 0),
    sold: (ms.sold ?? 0) + (ms.notariell_verkauft ?? 0),
  };

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-ink">
            {t('properties.title', 'Immobilien Management')}
          </h2>
          <p className="text-sm text-ink-3">
            {t('properties.subtitle', 'Alle Immobilien — Leads, aktive Vermarktung und Abschlüsse.')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper shadow-soft transition hover:bg-ink/90"
        >
          <Plus className="h-4 w-4" />
          {t('properties.create.cta', 'Neue Immobilie')}
        </button>
      </header>

      <div className="grid gap-4 md:grid-cols-5">
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('properties.stats.total', 'Gesamt')}</p>
          <p className="fm-kpi-value">{formatNumber(stats.total)}</p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('properties.stats.active', 'In Vermarktung')}</p>
          <p className="fm-kpi-value">{formatNumber(stats.active)}</p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('properties.stats.leads', 'Akquise')}</p>
          <p className="fm-kpi-value">{formatNumber(stats.leads)}</p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">
            {t('properties.stats.in_offer', 'Anbot/Notar')}
          </p>
          <p className="fm-kpi-value">{formatNumber(stats.inOffer)}</p>
        </div>
        <div className="fm-kpi !border-ink !bg-ink">
          <p className="fm-kpi-label !text-gold">{t('properties.stats.sold', 'Verkauft')}</p>
          <p className="fm-kpi-value !text-paper">{formatNumber(stats.sold)}</p>
        </div>
      </div>

      <div className="fm-card min-w-0 p-5 space-y-4">
        <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1" role="group" aria-label={t('properties.segment.label', 'Bereich')}>
          {PROPERTY_SEGMENTS.map((s) => {
            const active = segment === s;
            const n = segmentCount[s];
            return (
              <button
                key={s}
                type="button"
                aria-pressed={active}
                onClick={() => selectSegment(s)}
                className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-2 text-sm font-semibold transition ${
                  active
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-card text-ink-2 hover:bg-paper-2'
                }`}
              >
                {s === 'na_sajtu'
                  ? t('properties.segment.na_sajtu', 'Auf der Website')
                  : s === 'samo_na_sajtu'
                    ? t('properties.segment.samo_na_sajtu', 'Nur auf der Website')
                    : s === 'nije_na_sajtu'
                      ? t('properties.segment.nije_na_sajtu', 'Nicht auf der Website')
                      : t('properties.segment.sve', 'Alle')}
                {n != null && (
                  <span className={`text-xs font-normal ${active ? 'text-paper/70' : 'text-ink-3'}`}>
                    {formatNumber(n)}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* v2 stage chip strip — multi-select, URL-bound */}
        {!siteMode && <div className="flex flex-wrap gap-1.5">
          {STAGE_BUCKETS.map((b) => {
            const isActive = selectedBuckets.includes(b.key);
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => toggleBucket(b.key)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  isActive
                    ? 'border-[hsl(var(--brand-gold-deep))] bg-[hsl(var(--brand-gold)/0.12)] text-[hsl(var(--brand-gold-deep))]'
                    : 'border-line bg-card text-ink-3 hover:text-ink hover:bg-paper-2'
                }`}
              >
                {t(`properties.filters.chip.${b.key}`, b.fallback)}
              </button>
            );
          })}
        </div>}

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[12rem] max-w-md flex-1">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                siteMode
                  ? t('properties.site.search_placeholder', 'Suchen nach Titel, Ort, PLZ, Objektnummer, Makler …')
                  : t('properties.search.placeholder', 'Suchen nach Adresse, Stadt, EDI-Nummer …')
              }
              className="w-full rounded-md border border-line bg-card ps-9 pe-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            />
          </div>
          {!siteMode && (
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder={t('properties.filters.city', 'Stadt')}
              className="w-32 rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            />
          )}
          {/* Batch AQ — Moji/Svi scope toggle. Hidden for users without
              an assigned profile (e.g. unauthenticated edge case). */}
          {profile?.id && (
            <div className="flex gap-1 rounded-md bg-paper-2 p-1 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setActiveScope('mine')}
                className={`rounded px-2.5 py-1 ${
                  activeScope === 'mine'
                    ? 'bg-card text-ink shadow-soft'
                    : 'text-ink-3 hover:text-ink'
                }`}
              >
                {t('properties.scope.mine', 'Meine')}
              </button>
              <button
                type="button"
                onClick={() => setActiveScope('all')}
                className={`rounded px-2.5 py-1 ${
                  activeScope === 'all'
                    ? 'bg-card text-ink shadow-soft'
                    : 'text-ink-3 hover:text-ink'
                }`}
              >
                {t('properties.scope.all', 'Alle')}
              </button>
            </div>
          )}
          {!siteMode && <div className="flex gap-1 rounded-md bg-paper-2 p-1">
            <button
              type="button"
              onClick={() => setView('grid')}
              className={`inline-flex h-8 w-8 items-center justify-center rounded ${
                view === 'grid' ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
              }`}
              aria-label="Grid"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              className={`inline-flex h-8 w-8 items-center justify-center rounded ${
                view === 'list' ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
              }`}
              aria-label="List"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setView('map')}
              className={`inline-flex h-8 w-8 items-center justify-center rounded ${
                view === 'map' ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
              }`}
              aria-label="Map"
              title="Karte"
            >
              <Map className="h-4 w-4" />
            </button>
          </div>}
        </div>

        {narrowed && !listLoading && !listError && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line bg-paper-2 px-3 py-2 text-xs text-ink-2">
            <span>
              {stripTotal == null
                ? t('properties.segment.shown', 'Angezeigt: {{shown}}', { shown: shownLabel })
                : filtersActive
                  ? t('properties.segment.shown_of', 'Angezeigt: {{shown}} von {{total}} in diesem Bereich', {
                      shown: shownLabel,
                      total: formatNumber(stripTotal),
                    })
                  : t('properties.segment.shown_crm_total', 'Angezeigt: {{shown}} · insgesamt {{total}} Immobilien im CRM', {
                      shown: shownLabel,
                      total: formatNumber(stripTotal),
                    })}
            </span>
            <button type="button" onClick={showAll} className="font-semibold text-ink underline-offset-2 hover:underline">
              {t('properties.segment.show_all', 'Alle anzeigen')}
            </button>
          </div>
        )}

        {siteMode ? (
          siteQuery.isLoading ? (
            <p className="py-12 text-center text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
          ) : siteQuery.error ? (
            <p className="py-12 text-center text-sm text-[hsl(var(--danger))]">
              {humanizeApiError(
                (siteQuery.error as Error).message,
                t,
                t('properties.site.load_failed', 'Die Inserate konnten nicht geladen werden.'),
              )}
            </p>
          ) : siteItems.length === 0 ? (
            <p className="mx-auto max-w-lg py-12 text-center text-sm text-ink-3">
              {search
                ? t('properties.site.empty_search', 'Kein Inserat entspricht Ihrer Suche.')
                : siteScope === 'mine'
                  ? t('properties.site.empty_mine', 'Ihnen ist derzeit kein Inserat zugeordnet.')
                  : !counts
                    ? t('properties.site.empty_unknown', 'Derzeit werden keine Inserate angezeigt. Die Objekte im CRM finden Sie unter „Alle".')
                  : counts.on_site > 0
                    ? t('properties.site.empty_all_linked', 'Alle Inserate der Website sind mit einem Objekt im CRM verbunden.')
                    : t(
                        'properties.site.empty_no_sync',
                        'Der Abgleich mit der Website hat noch keine Inserate geliefert. Die Objekte im CRM finden Sie unter „Alle".',
                      )}
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {siteItems.map((l) => (
                <SiteListingCard
                  key={l.justimmo_id}
                  listing={l}
                  canAdd={l.can_add}
                  onOpen={() => openSiteListing(l)}
                  onAdd={() => setAddingListing(l)}
                />
              ))}
            </div>
          )
        ) : isLoading ? (
          <p className="py-12 text-center text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
        ) : error ? (
          <p className="py-12 text-center text-sm text-[hsl(var(--danger))]">
            {(error as Error).message}
          </p>
        ) : items.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-3">
            {filtersActive
              ? t('properties.segment.empty_filtered', 'Keine Immobilie entspricht den aktuellen Filtern.')
              : t('properties.empty', 'Keine Immobilien gefunden.')}
          </p>
        ) : view === 'grid' ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <PropertyCard
                key={p.id}
                property={p}
                isAdmin={isAdmin}
                onEdit={setEditing}
                compareSelected={compareIds.has(p.id)}
                onToggleCompare={() => toggleCompare(p.id)}
                listingImageUrl={listingImages?.get(p.id)}
              />
            ))}
          </div>
        ) : view === 'map' ? (
          <Suspense
            fallback={
              <p className="py-12 text-center text-sm text-ink-3">
                {t('common.loading', 'Lädt …')}
              </p>
            }
          >
            <PropertyMap properties={items} onPick={setViewing} />
          </Suspense>
        ) : (
          <ListView items={items} />
        )}

        {/* Infinite scroll footer — sentinel auto-fetches next 50 when
            in viewport. Visible across all three views (grid/list/map). */}
        {siteMode && siteItems.length > 0 && siteQuery.hasNextPage && (
          <div ref={siteSentinelRef} className="py-6 text-center text-xs text-ink-3">
            {siteQuery.isFetchingNextPage ? t('common.loading_more', 'Lädt weitere …') : ''}
          </div>
        )}
        {!siteMode && items.length > 0 && (
          <>
            {hasNextPage && (
              <div ref={sentinelRef} className="py-6 text-center text-xs text-ink-3">
                {isFetchingNextPage ? t('common.loading_more', 'Lädt weitere …') : ''}
              </div>
            )}
            {!hasNextPage && (
              <div className="border-t border-line pt-3 text-center text-[11px] uppercase tracking-wide text-ink-3/60">
                {t('common.end_of_list', 'Ende der Liste')}
              </div>
            )}
          </>
        )}
      </div>

      <CreatePropertyDialog open={creating} onOpenChange={setCreating} />
      {editing && (
        <EditPropertyDialog
          property={editing}
          open={true}
          onOpenChange={(o) => !o && setEditing(null)}
        />
      )}
      {viewing && (
        <ViewPropertyDialog
          property={viewing}
          isAdmin={isAdmin}
          open={true}
          onOpenChange={(o) => !o && setViewing(null)}
        />
      )}

      {openListing && (
        <SiteListingDialog
          listing={openListing}
          canAdd={openListing.can_add}
          onAdd={() => {
            setAddingListing(openListing);
            setOpenListing(null);
          }}
          onOpenChange={(o) => !o && setOpenListing(null)}
        />
      )}
      {addingListing && (
        <AddListingToCrmDialog
          listing={addingListing}
          onOpenChange={(o) => !o && setAddingListing(null)}
          onAdded={(propertyId) => {
            setAddingListing(null);
            navigate({ search: (p) => ({ ...p, open: propertyId }), replace: true });
          }}
        />
      )}

      {!siteMode && compareIds.size >= 2 && (
        <button
          type="button"
          onClick={() => setCompareOpen(true)}
          className="fixed bottom-6 left-1/2 z-30 -translate-x-1/2 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-paper shadow-elevated transition hover:bg-ink/90"
        >
          {t('properties.compare.cta', 'Vergleichen')} · {compareIds.size}
        </button>
      )}
      {compareOpen && (
        <PropertyCompareDialog
          properties={items.filter((p) => compareIds.has(p.id))}
          onClose={() => setCompareOpen(false)}
          onClear={() => setCompareIds(new Set())}
        />
      )}
    </section>
  );
}

function ListView({ items }: { items: PropertyRow[] }) {
  const { t } = useTranslation();
  return (
    <>
      {/* Mobile compact card list */}
      <div className="space-y-2 md:hidden">
        {items.map((p) => (
          <article key={p.id} className="rounded-md border border-line bg-card p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">{p.address ?? '—'}</p>
                <p className="mt-0.5 text-xs text-ink-3">
                  {[p.postal_code, p.district, p.city].filter(Boolean).join(' · ')}
                </p>
              </div>
              <span className="fm-pill fm-pill-neutral shrink-0">
                {t(`enums.marketing_stage.${p.marketing_stage}`, p.marketing_stage)}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-2">
              {p.building_type && (
                <span className="fm-pill fm-pill-neutral">
                  {t(`enums.building_type.${p.building_type}`, p.building_type)}
                </span>
              )}
              {p.object_type && <span>{p.object_type}</span>}
              {p.rooms_count && <span>{p.rooms_count} Zi</span>}
              {p.property_size && <span>{p.property_size} m²</span>}
              {p.purchase_price && (
                <span className="ms-auto font-medium text-ink">
                  € {formatNumber(Number(p.purchase_price))}
                </span>
              )}
            </div>
          </article>
        ))}
      </div>

      {/* Desktop table */}
      <div className="fm-table-wrap hidden md:block">
        <table className="fm-table">
          <thead>
            <tr>
              <th>{t('properties.cols.address', 'Adresse')}</th>
              <th>{t('properties.cols.city', 'Stadt')}</th>
              <th>{t('properties.cols.type', 'Typ')}</th>
              <th className="text-end">{t('properties.cols.rooms', 'Zimmer')}</th>
              <th className="text-end">{t('properties.cols.size', 'm²')}</th>
              <th className="text-end">{t('properties.cols.price', 'Preis')}</th>
              <th>{t('properties.cols.phase', 'Phase')}</th>
              <th>{t('properties.cols.agent', 'Makler')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td className="font-medium text-ink">{p.address ?? '—'}</td>
                <td>{p.city}</td>
                <td className="text-ink-2">{p.object_type ?? '—'}</td>
                <td className="text-end">{p.rooms_count ?? '—'}</td>
                <td className="text-end">{p.property_size ?? '—'}</td>
                <td className="text-end font-medium">
                  {p.purchase_price
                    ? formatMoney(Number(p.purchase_price))
                    : '—'}
                </td>
                <td>
                  <span className="fm-pill fm-pill-neutral">
                    {t(`enums.marketing_stage.${p.marketing_stage}`, p.marketing_stage)}
                  </span>
                </td>
                <td className="text-ink-2">
                  {p.assigned_agent_full_name ? (
                    <div className="inline-flex items-center gap-1.5">
                      <Avatar
                        src={
                          p.assigned_agent_avatar_key && p.assigned_agent_id_short
                            ? `/auth/avatar/${p.assigned_agent_id_short}?v=1`
                            : null
                        }
                        name={p.assigned_agent_full_name}
                        size="sm"
                      />
                      <span className="whitespace-nowrap">
                        {p.assigned_agent_full_name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-ink-3">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// Re-export — properties.leads.tsx and properties.active.tsx import this.
export function PropertiesTable({
  items,
  loading,
  error,
}: {
  items: PropertyRow[];
  loading: boolean;
  error: Error | null;
}) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const [editing, setEditing] = useState<PropertyRow | null>(null);

  if (loading) return <p className="py-8 text-center text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>;
  if (error)
    return <p className="py-8 text-center text-sm text-[hsl(var(--danger))]">{error.message}</p>;
  if (items.length === 0)
    return (
      <p className="py-8 text-center text-sm text-ink-3">
        {t('properties.empty', 'Keine Immobilien gefunden.')}
      </p>
    );

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <PropertyCard key={p.id} property={p} isAdmin={isAdmin} onEdit={setEditing} />
        ))}
      </div>
      {editing && (
        <EditPropertyDialog
          property={editing}
          open={true}
          onOpenChange={(o) => !o && setEditing(null)}
        />
      )}
    </>
  );
}
